import type { ElectronContext } from '@mimir/common/context/applicationContext';
import type { Logger } from '@mimir/common/types/logger';
import { isDirAvailableLocally } from '@mimir/common/util/fs';
import { getGlobalStore } from '@mimir/web/store';
import type { LauncherConfig } from './config';
import { executeJob } from './job';
import { getProjectFile } from './utils';
import type { LauncherState } from './state';

export const stopLauncher = (launcherState: LauncherState) => {
  if (launcherState.pollingIntervalId !== undefined) {
    clearInterval(launcherState.pollingIntervalId);
    launcherState.pollingIntervalId = undefined;
  }
};

/**
 * If configuration allows, this starts polling the watch folders every 5 seconds.
 *
 * Note that we need to use a getDownloadPath() function in case the download path changes
 * at runtime while the polling is going on.
 */
export const startLauncher = (
  services: {
    applicationContext: ElectronContext,
    logger: Logger,
    getDownloadPath: () => string,
  },
  launcherConfig: LauncherConfig,
  launcherState: LauncherState,
) => {
  stopLauncher(launcherState);
  if (!getGlobalStore().getters.hasTenantInfoAccess) {
    services.logger.info('startLauncher: Skipping for non-admin user.');
    return;
  }

  const config = launcherConfig;
  if (!config.enabled) {
    services.logger.info('startLauncher: driver disabled.');
    return;
  }

  const watchFolders = config.selectedWatchFolders;
  if (watchFolders.length <= 0) {
    services.logger.info('startLauncher: No watch folders configured.');
    return;
  }

  services.logger.info(`startLauncher: Starting to watch these folders: ${JSON.stringify(watchFolders, null, 2)}`);
  // TODO: is there a better way like fs events instead of listing the files?
  launcherState.pollingIntervalId = setInterval(() => {
    void scanFolders(services, launcherConfig, launcherState);
  }, 5000);
};

const processOneFolder = async (
  folderPath: string,
  services: {
    applicationContext: ElectronContext,
    logger: Logger,
    getDownloadPath: () => string,
  },
  launcherConfig: LauncherConfig,
): Promise<boolean> => {
  try {
    if (!await isDirAvailableLocally(services.applicationContext.nodeApi, services.logger, folderPath)) {
      // This folder does not exist or is not reachable.
      services.logger.warn(`Folder not found: ${folderPath}. Consider removing from AME/DVR settings.`);
      return false;
    }

    // Check if folder has a prproj file
    const projectPath = await getProjectFile(services.applicationContext, folderPath, launcherConfig.projectFileExtension);
    if (!projectPath) {
      // This folder does not seem to have any more projects. Let's continue
      // with the next watchfolder.
      return false;
    }

    if (!await executeJob(projectPath, services, launcherConfig)) {
      return false;
    }

  } catch (err) {
    services.logger.error(`Error in processFolder: ${err}`);
  }

  return true;
};

/**
 * The main processing code is here. We rotate through the list of configured watch folders and look
 * for the first .prproj file we can find. As soon as we find one, we try to launch AME/DVR to process it
 * with the right preset. While AME/DVR is running, we check for progress by polling a temp file. After
 * done, we clean up. The next invocation continues with the next watch folder in the list.
 */
const scanFolders = async (
  services: {
    applicationContext: ElectronContext,
    logger: Logger,
    getDownloadPath: () => string,
  },
  launcherConfig: LauncherConfig,
  launcherState: LauncherState,
) => {
  // This is called automatically every 5 seconds. If the previous invocation is still running, it will
  // just skip the current invocation.
  if (launcherState.isProcessingAlready) {
    return;
  }

  try {
    launcherState.isProcessingAlready = true;
    const watchFolders = launcherConfig.selectedWatchFolders as string[];
    let atLeastOneSuccessDuringThisScan = false;

    do {
      atLeastOneSuccessDuringThisScan = false;

      // Scan all watchfolders one by one. As soon as we find a project, we try to process
      // it, and then continue with the next watchfolder without looking for more projects
      // in this same folder. This is just to have a little bit of fairness amongst all
      // configured folders through a round-robin mechanism, so that no single folder with
      // too many projects can starve other folders with a very few projects.
      for (const folderPath of watchFolders) {
        if (await processOneFolder(folderPath, services, launcherConfig)) {
          atLeastOneSuccessDuringThisScan = true;
        }
      }

      // While there was at least one project we could process successfully, it makes sense to immediately
      // look for more in the watchfolders and rerun the scan. If there were no projects successful in this
      // round, we should just quit this loop, and let the outer code take care of triggering the same
      // scanning again in every 5s interval.
    } while (atLeastOneSuccessDuringThisScan);
  } catch (err) {
    services.logger.error(`Error in scanFolders: ${err}`);
  } finally {
    launcherState.isProcessingAlready = false;
  }
};
